import * as DateF from 'date-fns'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { css } from 'styled-components'

import { Result } from '@blade/std-lib'
import { Button, DownIcon } from '@blade/ui-kit'
import { CopyIcon } from '@blade/ui-kit'
import { PlatformUtil } from '@root/shared/core'
import { Dialog, ShareArrowIcon, useSnackbar } from '@root/shared/ui-kit'

import { PnlPositionCardCanvas } from './pnl-position-card-canvas'

import type { Pair } from '@root/entities/pair'
import type { WalletPositionModel } from '@root/entities/position'
import type { ReferralCode } from '@root/entities/referral/model/referral-code'
import type { UserShort } from '@root/entities/user-short'
import type { CanvasPnlPositionBuilder } from '@root/features/trade/position/pnl-card/ui/canvas-pnl-position-builder'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  position: WalletPositionModel
  pair: Pair.PairMinimal
  user: UserShort.UserShort
  referralCode: ReferralCode | undefined
}

export const PnlPositionShareDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  position,
  user,
  pair,
  referralCode,
}) => {
  const rendererRef = useRef<CanvasPnlPositionBuilder | undefined>(undefined)
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const handleDownload = async () => {
    const renderer = rendererRef.current
    if (!renderer) {
      return
    }

    const dataUrl = renderer.toDataUrl()
    const fileName = `kol.trade PnL ${pair.quote.ticker} (${DateF.format(new Date(), 'yyyy-MM-dd')}).png`

    let downloadResult = await PlatformUtil.downloadFileFromURL(
      dataUrl,
      fileName,
    )

    if (Result.isFailure(downloadResult)) {
      const blob = await renderer.toBlob()
      if (blob) {
        downloadResult = await PlatformUtil.downloadFile(blob, fileName)
      }
    }

    if (Result.isSuccess(downloadResult)) {
      showSnackbar({
        intent: 'success',
        text: t('profile.best_deal.download_success'),
        delay: 1500,
      })
    } else {
      showSnackbar({
        intent: 'error',
        text: t('profile.best_deal.download_error'),
        delay: 1500,
      })
    }
  }

  const handleShare = async () => {
    const renderer = rendererRef.current
    if (!renderer) {
      return
    }

    const fileName = `kol.trade PnL ${position.pairAddress} (${Date.now()}).png`
    const blob = await renderer.toBlob()

    if (blob) {
      const shareResult = await PlatformUtil.share({
        files: [new File([blob], fileName, { type: 'image/png' })],
      })
      return Result.isSuccess(shareResult)
    }
    return false
  }

  const handleCopyImageToClipboard = async () => {
    const renderer = rendererRef.current

    if (!renderer) {
      return
    }

    const blob = await renderer.toBlob()

    if (!blob) {
      return
    }

    const copyResult = await PlatformUtil.copyImageToClipboard(
      blob,
      'image/png',
    )
    if (Result.isSuccess(copyResult)) {
      showSnackbar({
        intent: 'success',
        text: t('common.copied-to-clipboard'),
        delay: 1500,
      })
    }
  }

  return (
    <Dialog
      variant="functional"
      open={open}
      onOpenChange={onOpenChange}
      describedBy={t('trade.pnl.share.title')}
    >
      <Dialog.Header.Functional title={t('trade.pnl.share.title')} />
      <Dialog.Content.Functional
        $variant={0}
        css={css`
          position: relative;
          overflow: hidden;
        `}
      >
        <Content>
          <PnlPositionCardCanvas
            user={user}
            referralCode={referralCode}
            position={position}
            pair={pair}
            rendererRef={rendererRef}
          />
        </Content>
        <Gradient>
          <ButtonWrapper>
            <ButtonAlt
              onClick={handleShare}
              size="m"
              variant="soft"
              radius="rounded"
              fullWidth
              iconLeft={<ShareArrowIcon />}
            />
            <ButtonAlt
              onClick={handleCopyImageToClipboard}
              size="m"
              variant="soft"
              radius="rounded"
              fullWidth
              iconLeft={<CopyIcon />}
            />
            <ButtonAlt
              onClick={handleDownload}
              size="m"
              color="primary"
              radius="rounded"
              fullWidth
              iconLeft={<DownIcon />}
            >
              {t('common.download')}
            </ButtonAlt>
          </ButtonWrapper>
        </Gradient>
      </Dialog.Content.Functional>
    </Dialog>
  )
}

const Content = styled.div`
  width: 420px;
  height: 520px;
  margin-bottom: 60px;
`

const ButtonWrapper = styled.div`
  padding: 0 16px 0;
  display: grid;
  gap: 8px;
  grid-template-columns: 0.5fr 0.5fr 1fr;

  background: linear-gradient(0deg, rgb(0 0 0 / 90%) 60%, transparent 100%);

  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  padding-top: 44px;
  padding-inline: 38px;
`

const Gradient = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  width: 100%;
  z-index: 1;
`

const ButtonAlt = styled(Button)`
  padding-inline: 10px;
`
